import { useState, useEffect, useCallback, useRef } from "react";
import { ArrowLeft, Trash2, Plus, RefreshCw, Eye, BarChart3, Settings, Image, MessageSquare, HelpCircle, Layers, Lock, Edit2, Save, X, Star, Upload, GripVertical, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

const statusMap = {
  new: { label: "Nouveau", color: "bg-blue-100 text-blue-800" },
  contacted: { label: "Contacté", color: "bg-yellow-100 text-yellow-800" },
  completed: { label: "Complété", color: "bg-green-100 text-green-800" },
  cancelled: { label: "Annulé", color: "bg-gray-100 text-gray-700" },
};

const categoryOptions = [
  { value: "tonte", label: "Tonte de gazon" },
  { value: "entretien", label: "Entretien de terrain" },
  { value: "piscine", label: "Contour de piscine" },
  { value: "vitres", label: "Lavage de vitres" },
  { value: "abris", label: "Démontage d'abris auto" },
  { value: "divers", label: "Divers travaux" },
];

const tabs = [
  { id: "overview", label: "Aperçu", icon: BarChart3 },
  { id: "quotes", label: "Demandes", icon: MessageSquare },
  { id: "services", label: "Services", icon: Layers },
  { id: "testimonials", label: "Témoignages", icon: Star },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "gallery", label: "Galerie", icon: Image },
  { id: "settings", label: "Paramètres", icon: Settings },
];

export default function AdminDashboard() {
  const [auth, setAuth] = useState(false);
  const [pw, setPw] = useState("");
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({ stats: null, quotes: [], services: [], testimonials: [], faq: [], gallery: [], settings: {} });
  const [loading, setLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => { if (sessionStorage.getItem("admin_auth") === "true") setAuth(true); }, []);

  const login = (e) => {
    e.preventDefault();
    if (pw === ADMIN_PASSWORD) { setAuth(true); sessionStorage.setItem("admin_auth", "true"); toast.success("Accès autorisé"); }
    else toast.error("Mot de passe incorrect");
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [st, q, sv, t, f, g, se] = await Promise.all([
        axios.get(`${API}/admin/stats`), axios.get(`${API}/admin/quotes`),
        axios.get(`${API}/admin/services`), axios.get(`${API}/admin/testimonials`),
        axios.get(`${API}/admin/faq`), axios.get(`${API}/gallery`),
        axios.get(`${API}/settings`)
      ]);
      setData({ stats: st.data, quotes: q.data, services: sv.data, testimonials: t.data, faq: f.data, gallery: g.data, settings: se.data });
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (auth) fetchAll(); }, [auth, fetchAll]);

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString("fr-CA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

  // Login screen
  if (!auth) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="admin-dashboard">
      <form onSubmit={login} className="bg-white rounded-xl border shadow-sm p-8 w-full max-w-sm" data-testid="admin-login-form">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-[#2D5A3D] flex items-center justify-center mx-auto mb-3"><Lock className="w-6 h-6 text-white" /></div>
          <h2 className="text-xl font-bold text-gray-900">Administration</h2>
        </div>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Mot de passe" className="mb-4" data-testid="admin-password-input" autoFocus />
        <Button type="submit" className="w-full bg-[#2D5A3D] hover:bg-[#234A30] text-white" data-testid="admin-login-btn">Accéder</Button>
        <a href="/" className="block text-center text-sm text-gray-500 hover:text-[#2D5A3D] mt-4">Retour au site</a>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#2D5A3D] text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-white hover:text-green-200" data-testid="admin-back-link"><ArrowLeft className="w-5 h-5" /></a>
            <h1 className="text-lg font-bold" data-testid="admin-title">Tableau de bord</h1>
          </div>
          <Button onClick={fetchAll} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10 hover:text-white" disabled={loading} data-testid="admin-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Actualiser
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-[#2D5A3D] text-white" : "text-gray-600 hover:bg-gray-100"}`}
                data-testid={`tab-${t.id}`}>
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Overview */}
        {tab === "overview" && data.stats && (
          <div className="space-y-6" data-testid="admin-stats">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { id: "stat-total", label: "Total demandes", val: data.stats.total_quotes, color: "text-gray-900" },
                { id: "stat-new", label: "Nouvelles", val: data.stats.new_quotes, color: "text-blue-600" },
                { id: "stat-contacted", label: "Contactées", val: data.stats.contacted_quotes, color: "text-yellow-600" },
                { id: "stat-completed", label: "Complétées", val: data.stats.completed_quotes, color: "text-green-600" },
              ].map((s) => (
                <div key={s.id} className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-gray-500">{s.label}</p>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 border"><p className="text-sm text-gray-500">Services</p><p className="text-xl font-bold">{data.stats.services_count}</p></div>
              <div className="bg-white rounded-lg p-4 border"><p className="text-sm text-gray-500">Témoignages</p><p className="text-xl font-bold">{data.stats.testimonials_count}</p></div>
              <div className="bg-white rounded-lg p-4 border"><p className="text-sm text-gray-500">Photos galerie</p><p className="text-xl font-bold">{data.stats.gallery_count}</p></div>
            </div>
          </div>
        )}

        {/* Quotes */}
        {tab === "quotes" && (
          <QuotesTab quotes={data.quotes} onRefresh={fetchAll} formatDate={formatDate} selectedQuote={selectedQuote} setSelectedQuote={setSelectedQuote} />
        )}

        {/* Services */}
        {tab === "services" && <ServicesTab services={data.services} onRefresh={fetchAll} />}

        {/* Testimonials */}
        {tab === "testimonials" && <TestimonialsTab testimonials={data.testimonials} onRefresh={fetchAll} />}

        {/* FAQ */}
        {tab === "faq" && <FAQTab faq={data.faq} onRefresh={fetchAll} />}

        {/* Gallery */}
        {tab === "gallery" && <GalleryTab gallery={data.gallery} services={data.services} onRefresh={fetchAll} />}

        {/* Settings */}
        {tab === "settings" && <SettingsTab settings={data.settings} onRefresh={fetchAll} />}
      </div>
    </div>
  );
}

// ========== QUOTES TAB ==========
function QuotesTab({ quotes, onRefresh, formatDate, selectedQuote, setSelectedQuote }) {
  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/quotes/${id}`, { status });
    toast.success("Statut mis à jour");
    onRefresh();
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette demande?")) return;
    await axios.delete(`${API}/admin/quotes/${id}`);
    toast.success("Supprimé");
    onRefresh();
  };

  return (
    <>
      <div className="bg-white rounded-lg border overflow-hidden" data-testid="quotes-table-container">
        {quotes.length === 0 ? <p className="p-8 text-center text-gray-500">Aucune demande.</p> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-gray-50">
                <TableHead>Date</TableHead><TableHead>Nom</TableHead><TableHead>Tél</TableHead>
                <TableHead className="hidden md:table-cell">Services</TableHead><TableHead className="hidden md:table-cell">Ville</TableHead>
                <TableHead>Statut</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {quotes.map((q) => (
                  <TableRow key={q.id}>
                    <TableCell className="text-xs text-gray-500 whitespace-nowrap">{formatDate(q.created_at)}</TableCell>
                    <TableCell className="font-medium">{q.name}</TableCell>
                    <TableCell><a href={`tel:${q.phone}`} className="text-[#2D5A3D] hover:underline">{q.phone}</a></TableCell>
                    <TableCell className="hidden md:table-cell text-xs">{(q.services || []).join(", ") || q.service || "-"}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{q.city || "-"}</TableCell>
                    <TableCell>
                      <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                        <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(statusMap).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setSelectedQuote(q)} className="h-8 w-8"><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => del(q.id)} className="h-8 w-8 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="sm:max-w-md" data-testid="quote-detail-dialog">
          <DialogHeader><DialogTitle>Détails de la demande</DialogTitle><DialogDescription>Informations complètes</DialogDescription></DialogHeader>
          {selectedQuote && (
            <div className="space-y-3 text-sm">
              <div><span className="font-medium text-gray-500">Nom:</span><p>{selectedQuote.name}</p></div>
              <div><span className="font-medium text-gray-500">Tél:</span><p><a href={`tel:${selectedQuote.phone}`} className="text-[#2D5A3D]">{selectedQuote.phone}</a></p></div>
              {selectedQuote.email && <div><span className="font-medium text-gray-500">Courriel:</span><p>{selectedQuote.email}</p></div>}
              {selectedQuote.city && <div><span className="font-medium text-gray-500">Ville:</span><p>{selectedQuote.city}</p></div>}
              <div><span className="font-medium text-gray-500">Services:</span><p>{(selectedQuote.services || []).join(", ") || selectedQuote.service || "-"}</p></div>
              {selectedQuote.message && <div><span className="font-medium text-gray-500">Message:</span><p className="whitespace-pre-wrap">{selectedQuote.message}</p></div>}
              <div><span className="font-medium text-gray-500">Préférence:</span><p>{selectedQuote.contact_preference || "Téléphone"}</p></div>
              <div><span className="font-medium text-gray-500">Statut:</span><Badge className={`ml-2 ${statusMap[selectedQuote.status]?.color}`}>{statusMap[selectedQuote.status]?.label}</Badge></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ========== SERVICES TAB ==========
function ServicesTab({ services, onRefresh }) {
  const [form, setForm] = useState({ name: "", description: "", icon: "Wrench", image: "", sub_services: "", order: 0, featured: false });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    const payload = { ...form, sub_services: form.sub_services ? form.sub_services.split(",").map(s => s.trim()).filter(Boolean) : [], order: parseInt(form.order) || 0 };
    if (editing) { await axios.patch(`${API}/admin/services/${editing}`, payload); toast.success("Service mis à jour"); setEditing(null); }
    else { await axios.post(`${API}/admin/services`, payload); toast.success("Service ajouté"); }
    setForm({ name: "", description: "", icon: "Wrench", image: "", sub_services: "", order: 0, featured: false });
    onRefresh();
  };

  const del = async (id) => { if (!window.confirm("Supprimer ce service?")) return; await axios.delete(`${API}/admin/services/${id}`); toast.success("Supprimé"); onRefresh(); };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ name: s.name, description: s.description, icon: s.icon, image: s.image, sub_services: (s.sub_services || []).join(", "), order: s.order, featured: s.featured });
  };

  return (
    <div className="space-y-6" data-testid="services-admin">
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">{editing ? "Modifier le service" : "Ajouter un service"}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} placeholder="Nom du service" />
          <Input value={form.icon} onChange={(e) => setForm(p => ({...p, icon: e.target.value}))} placeholder="Icône (Leaf, TreePine, etc.)" />
          <Input value={form.image} onChange={(e) => setForm(p => ({...p, image: e.target.value}))} placeholder="URL image (ex: /api/photos/nom.jpg)" className="sm:col-span-2" />
          <Textarea value={form.description} onChange={(e) => setForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="sm:col-span-2 resize-none" rows={2} />
          <Input value={form.sub_services} onChange={(e) => setForm(p => ({...p, sub_services: e.target.value}))} placeholder="Sous-services (séparés par virgule)" className="sm:col-span-2" />
          <Input type="number" value={form.order} onChange={(e) => setForm(p => ({...p, order: e.target.value}))} placeholder="Ordre d'affichage" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm(p => ({...p, featured: e.target.checked}))} className="w-4 h-4 text-[#2D5A3D]" />Service vedette (affiché en haut)</label>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={save} className="bg-[#2D5A3D] hover:bg-[#234A30] text-white"><Save className="w-4 h-4 mr-1" />{editing ? "Sauvegarder" : "Ajouter"}</Button>
          {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm({ name: "", description: "", icon: "Wrench", image: "", sub_services: "", order: 0, featured: false }); }}><X className="w-4 h-4 mr-1" />Annuler</Button>}
        </div>
      </div>
      <div className="bg-white rounded-lg border divide-y">
        {services.map((s) => (
          <div key={s.id} className="p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {s.image && <img src={s.image} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" onError={(e) => e.target.style.display = 'none'} />}
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{s.name}</span>
                  {s.featured && <Badge className="bg-green-100 text-green-800 text-xs">Vedette</Badge>}
                  {!s.active && <Badge className="bg-gray-100 text-gray-600 text-xs">Inactif</Badge>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{s.description}</p>
                <p className="text-xs text-gray-400">Ordre: {s.order}</p>
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <Button variant="ghost" size="icon" onClick={() => startEdit(s)} className="h-8 w-8"><Edit2 className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => del(s.id)} className="h-8 w-8 text-red-500"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========== TESTIMONIALS TAB ==========
function TestimonialsTab({ testimonials, onRefresh }) {
  const [form, setForm] = useState({ name: "", text: "", rating: 5, city: "" });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.name || !form.text) {
      toast.error("Veuillez remplir le nom et le témoignage");
      return;
    }
    try {
      if (editing) { 
        await axios.patch(`${API}/admin/testimonials/${editing}`, form); 
        toast.success("Témoignage mis à jour"); 
        setEditing(null); 
      } else { 
        await axios.post(`${API}/admin/testimonials`, form); 
        toast.success("Témoignage ajouté"); 
      }
      setForm({ name: "", text: "", rating: 5, city: "" });
      onRefresh();
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const del = async (id) => { 
    if (!window.confirm("Supprimer ce témoignage?")) return; 
    await axios.delete(`${API}/admin/testimonials/${id}`); 
    toast.success("Témoignage supprimé"); 
    onRefresh(); 
  };

  const startEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, text: t.text, rating: t.rating, city: t.city || "" });
  };

  return (
    <div className="space-y-6" data-testid="testimonials-admin">
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          {editing ? "Modifier le témoignage" : "Ajouter un témoignage"}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input 
            value={form.name} 
            onChange={(e) => setForm(p => ({...p, name: e.target.value}))} 
            placeholder="Nom du client (ex: Marie L.)" 
          />
          <Input 
            value={form.city} 
            onChange={(e) => setForm(p => ({...p, city: e.target.value}))} 
            placeholder="Ville (ex: Châteauguay)" 
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
            <div className="flex gap-1">
              {[1,2,3,4,5].map(n => (
                <button 
                  key={n} 
                  type="button" 
                  onClick={() => setForm(p => ({...p, rating: n}))}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <Star className={`w-6 h-6 ${n <= form.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <Textarea 
            value={form.text} 
            onChange={(e) => setForm(p => ({...p, text: e.target.value}))} 
            placeholder="Le témoignage du client..." 
            className="sm:col-span-3 resize-none" 
            rows={3} 
          />
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={save} className="bg-[#2D5A3D] hover:bg-[#234A30] text-white">
            <Save className="w-4 h-4 mr-1" />{editing ? "Sauvegarder" : "Ajouter le témoignage"}
          </Button>
          {editing && (
            <Button variant="outline" onClick={() => { setEditing(null); setForm({ name: "", text: "", rating: 5, city: "" }); }}>
              <X className="w-4 h-4 mr-1" />Annuler
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b bg-gray-50">
          <h4 className="font-medium text-gray-700">Témoignages existants ({testimonials.length})</h4>
        </div>
        {testimonials.length === 0 ? (
          <p className="p-8 text-center text-gray-500">Aucun témoignage. Ajoutez-en un ci-dessus.</p>
        ) : (
          <div className="divide-y">
            {testimonials.map((t) => (
              <div key={t.id} className="p-4 flex items-start justify-between gap-4 hover:bg-gray-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-medium text-gray-900">{t.name}</p>
                    {t.city && <span className="text-gray-400 text-sm">— {t.city}</span>}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">"{t.text}"</p>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={`${t.id}-star-${i}`} className={`w-4 h-4 ${i < t.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`} />
                    ))}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(t)} className="h-8 w-8" title="Modifier">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => del(t.id)} className="h-8 w-8 text-red-500 hover:text-red-700" title="Supprimer">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ========== FAQ TAB ==========
function FAQTab({ faq, onRefresh }) {
  const [form, setForm] = useState({ question: "", answer: "", order: 0 });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.question || !form.answer) {
      toast.error("Veuillez remplir la question et la réponse");
      return;
    }
    const payload = { ...form, order: parseInt(form.order) || 0 };
    if (editing) { await axios.patch(`${API}/admin/faq/${editing}`, payload); toast.success("Question mise à jour"); setEditing(null); }
    else { await axios.post(`${API}/admin/faq`, payload); toast.success("Question ajoutée"); }
    setForm({ question: "", answer: "", order: 0 });
    onRefresh();
  };

  const del = async (id) => { if (!window.confirm("Supprimer cette question?")) return; await axios.delete(`${API}/admin/faq/${id}`); toast.success("Supprimé"); onRefresh(); };

  return (
    <div className="space-y-6" data-testid="faq-admin">
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">{editing ? "Modifier la question" : "Ajouter une question FAQ"}</h3>
        <div className="space-y-3">
          <Input value={form.question} onChange={(e) => setForm(p => ({...p, question: e.target.value}))} placeholder="Question (ex: Combien coûte une soumission?)" />
          <Textarea value={form.answer} onChange={(e) => setForm(p => ({...p, answer: e.target.value}))} placeholder="Réponse..." className="resize-none" rows={3} />
          <Input type="number" value={form.order} onChange={(e) => setForm(p => ({...p, order: e.target.value}))} placeholder="Ordre d'affichage" className="w-32" />
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={save} className="bg-[#2D5A3D] hover:bg-[#234A30] text-white"><Save className="w-4 h-4 mr-1" />{editing ? "Sauvegarder" : "Ajouter"}</Button>
          {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm({ question: "", answer: "", order: 0 }); }}>Annuler</Button>}
        </div>
      </div>
      <div className="bg-white rounded-lg border divide-y">
        {faq.length === 0 ? (
          <p className="p-8 text-center text-gray-500">Aucune question FAQ.</p>
        ) : faq.map((f) => (
          <div key={f.id} className="p-4 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">{f.question}</p>
              <p className="text-sm text-gray-600 mt-1">{f.answer}</p>
              <p className="text-xs text-gray-400 mt-1">Ordre: {f.order}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <Button variant="ghost" size="icon" onClick={() => { setEditing(f.id); setForm({ question: f.question, answer: f.answer, order: f.order }); }} className="h-8 w-8"><Edit2 className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => del(f.id)} className="h-8 w-8 text-red-500"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========== GALLERY TAB (ENHANCED) ==========
function GalleryTab({ gallery, services, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ caption: "", category: "" });
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const upload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("caption", caption);
    formData.append("category", category);
    if (serviceId) formData.append("service_id", serviceId);

    try {
      await axios.post(`${API}/admin/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo ajoutée avec succès!");
      setCaption("");
      setCategory("");
      setServiceId("");
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const updateImage = async (id) => {
    try {
      await axios.patch(`${API}/admin/gallery/${id}`, editForm);
      toast.success("Image mise à jour");
      setEditingId(null);
      onRefresh();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const del = async (id) => { 
    if (!window.confirm("Supprimer cette image?")) return; 
    await axios.delete(`${API}/admin/gallery/${id}`); 
    toast.success("Image supprimée"); 
    onRefresh(); 
  };

  const startEdit = (img) => {
    setEditingId(img.id);
    setEditForm({ caption: img.caption || "", category: img.category || "" });
  };

  return (
    <div className="space-y-6" data-testid="gallery-admin">
      {/* Upload Section */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#2D5A3D]" />
          Ajouter une photo
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Area */}
          <div>
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${previewUrl ? 'border-[#2D5A3D] bg-green-50' : 'border-gray-300 hover:border-[#2D5A3D]'}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,.heic"
                className="hidden"
              />
              {previewUrl ? (
                <div className="space-y-3">
                  <img src={previewUrl} alt="Aperçu" className="max-h-48 mx-auto rounded-lg object-contain" />
                  <p className="text-sm text-[#2D5A3D] font-medium flex items-center justify-center gap-1">
                    <Check className="w-4 h-4" /> Image sélectionnée
                  </p>
                  <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPreviewUrl(null); if(fileInputRef.current) fileInputRef.current.value = ""; }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Changer l'image
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Image className="w-12 h-12 mx-auto text-gray-400" />
                  <p className="text-sm text-gray-600">Cliquez pour sélectionner une image</p>
                  <p className="text-xs text-gray-400">JPG, PNG, WEBP ou HEIC</p>
                </div>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Légende (optionnel)</label>
              <Input 
                value={caption} 
                onChange={(e) => setCaption(e.target.value)} 
                placeholder="Ex: Tonte de gazon à Châteauguay" 
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue placeholder="Sélectionner une catégorie" /></SelectTrigger>
                <SelectContent>
                  {categoryOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Associer à un service (optionnel)</label>
              <Select value={serviceId || "none"} onValueChange={(v) => setServiceId(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Aucun - galerie seulement" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun - galerie seulement</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Si sélectionné, cette image deviendra la photo principale du service</p>
            </div>

            <Button 
              onClick={upload} 
              disabled={uploading || !previewUrl} 
              className="w-full bg-[#2D5A3D] hover:bg-[#234A30] text-white"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Upload en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Ajouter la photo
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <h4 className="font-medium text-gray-700">Photos dans la galerie ({gallery.length})</h4>
        </div>
        
        {gallery.length === 0 ? (
          <p className="p-8 text-center text-gray-500">Aucune image dans la galerie. Ajoutez-en une ci-dessus.</p>
        ) : (
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((img) => (
              <div key={img.id} className="relative group bg-gray-100 rounded-lg overflow-hidden">
                <div className="aspect-square">
                  <img 
                    src={img.url} 
                    alt={img.caption || "Photo"} 
                    className="w-full h-full object-cover" 
                    onError={(e) => { 
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999' font-size='14'%3EImage non disponible%3C/text%3E%3C/svg%3E"; 
                    }} 
                  />
                </div>
                
                {/* Overlay with info */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-end">
                  <div className="w-full p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                    {editingId === img.id ? (
                      <div className="bg-white rounded p-2 space-y-2" onClick={e => e.stopPropagation()}>
                        <Input 
                          value={editForm.caption} 
                          onChange={(e) => setEditForm(p => ({...p, caption: e.target.value}))} 
                          placeholder="Légende"
                          className="h-8 text-xs"
                        />
                        <Select value={editForm.category} onValueChange={(v) => setEditForm(p => ({...p, category: v}))}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Catégorie" /></SelectTrigger>
                          <SelectContent>
                            {categoryOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex gap-1">
                          <Button size="sm" className="h-7 text-xs flex-1 bg-[#2D5A3D]" onClick={() => updateImage(img.id)}>
                            <Save className="w-3 h-3 mr-1" />Sauver
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingId(null)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-white text-xs">
                        {img.caption && <p className="truncate">{img.caption}</p>}
                        {img.category && (
                          <Badge className="bg-white/20 text-white text-xs mt-1">
                            {categoryOptions.find(c => c.value === img.category)?.label || img.category}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                {editingId !== img.id && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button 
                      variant="secondary" 
                      size="icon" 
                      className="h-7 w-7 bg-white/90 hover:bg-white" 
                      onClick={() => startEdit(img)}
                      title="Modifier"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="icon" 
                      className="h-7 w-7" 
                      onClick={() => del(img.id)}
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ========== SETTINGS TAB ==========
function SettingsTab({ settings, onRefresh }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings || {}); }, [settings]);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API}/admin/settings`, form); toast.success("Paramètres sauvegardés"); onRefresh(); }
    catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm(p => ({...p, [k]: v}));

  return (
    <div className="space-y-6" data-testid="settings-admin">
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">Page d'accueil</h3>
        <div className="space-y-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Titre principal</label><Input value={form.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Sous-titre</label><Textarea value={form.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} rows={2} className="resize-none" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Texte du bouton CTA</label><Input value={form.hero_cta || ""} onChange={(e) => set("hero_cta", e.target.value)} /></div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">SEO</h3>
        <div className="space-y-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Meta titre</label><Input value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Meta description</label><Textarea value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} rows={2} className="resize-none" /></div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">Contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Courriel</label><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">Bannière / Promotion</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.banner_active || false} onChange={(e) => set("banner_active", e.target.checked)} className="w-4 h-4" />Activer la bannière</label>
          <Input value={form.banner_text || ""} onChange={(e) => set("banner_text", e.target.value)} placeholder="Texte de la bannière" />
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#2D5A3D] hover:bg-[#234A30] text-white px-8">
        <Save className="w-4 h-4 mr-2" />{saving ? "Sauvegarde..." : "Sauvegarder les paramètres"}
      </Button>
    </div>
  );
}
